import {
  VEHICLE_CATALOG_NAME_MAX,
  type AdminVehicleCatalogMakeDetailDto,
  type AdminVehicleCatalogModelDto,
  type AdminVehicleCatalogPatchMakeBody,
  type AdminVehicleCatalogPatchModelBody,
} from '@carcheck/shared';
import { z } from 'zod';

/**
 * Forms of «Справочник авто» (AUT-437). Zod messages are i18n keys (rule 11),
 * translated at render. A name is ≤ `VEHICLE_CATALOG_NAME_MAX` (50) — the
 * API refuses a longer one with a 422, never clips it.
 *
 * PATCH bodies carry ONLY the changed keys (the API 400s a body with nothing
 * but `updatedAt`), and `updatedAt` is the server's string as received.
 */
export const NAME_MAX = VEHICLE_CATALOG_NAME_MAX;

export const catalogName = z
  .string()
  .trim()
  .min(1, 'vehicleCatalog.validation.required')
  .max(NAME_MAX, 'vehicleCatalog.validation.tooLong');

export const makeFormSchema = z.object({
  name: catalogName,
  popular: z.boolean(),
  onSale: z.boolean(),
  hidden: z.boolean(),
});
export type MakeFormValues = z.infer<typeof makeFormSchema>;

export const modelFormSchema = makeFormSchema.extend({ legacy: z.boolean() });
export type ModelFormValues = z.infer<typeof modelFormSchema>;

export const aliasFormSchema = z.object({ alias: catalogName });
export type AliasFormValues = z.infer<typeof aliasFormSchema>;

type Row = Pick<AdminVehicleCatalogModelDto, 'name' | 'popular' | 'onSale' | 'status'>;

export const makeValues = (m: Row): MakeFormValues => ({
  name: m.name,
  popular: m.popular,
  onSale: m.onSale,
  hidden: m.status === 'hidden',
});

export const modelValues = (m: AdminVehicleCatalogModelDto): ModelFormValues => ({
  ...makeValues(m),
  legacy: m.legacy,
});

type Changes = Omit<AdminVehicleCatalogPatchModelBody, 'updatedAt'>;

const changesOf = (
  v: MakeFormValues & { legacy?: boolean },
  row: MakeFormValues & { legacy?: boolean },
): Changes => {
  const out: Changes = {};
  const name = v.name.trim();
  if (name !== row.name) out.name = name;
  if (v.popular !== row.popular) out.popular = v.popular;
  if (v.onSale !== row.onSale) out.onSale = v.onSale;
  if (v.hidden !== row.hidden) out.status = v.hidden ? 'hidden' : 'active';
  if (v.legacy !== undefined && v.legacy !== row.legacy) out.legacy = v.legacy;
  return out;
};

/** `null` = nothing changed (Save stays disabled). */
export const makePatchBody = (
  v: MakeFormValues,
  make: AdminVehicleCatalogMakeDetailDto,
): AdminVehicleCatalogPatchMakeBody | null => {
  const changes = changesOf(v, makeValues(make));
  return Object.keys(changes).length === 0 ? null : { updatedAt: make.updatedAt, ...changes };
};

export const modelPatchBody = (
  v: ModelFormValues,
  model: AdminVehicleCatalogModelDto,
): AdminVehicleCatalogPatchModelBody | null => {
  const changes = changesOf(v, modelValues(model));
  return Object.keys(changes).length === 0 ? null : { updatedAt: model.updatedAt, ...changes };
};
